import React, { useEffect, useState } from "react";
import { api } from "./api";
import { AllergyBanner, type AllergyPanelData } from "./AllergyBanner";
import { MedStatusBadge } from "./MedStatusBadge";
import { useProblems, FieldProblem, invalid, type Rule } from "./form-check";
import { appNow } from "./clock";
import { easternMinutes } from "../server/station/windows";
import { stamp, DISPOSITION_LABEL, type AdminView } from "./Pass";
import type { MedStatus } from "../server/station/med-status";

/**
 * Give an OTC item (build plan §10.1 #2; Baseline §5.2): the person's OTC
 * permissions, the Rx list and the allergy banner (medication allergies
 * first) for the clinician's judgment — no clinical gate (§8.8). The record
 * it makes is enforced on the server: a call before a Call-me-first dose, the
 * override log for a "No" item, a reason past the interval warning, the
 * consideration tick, a visit for a complaint. Usable on a phone (375 px).
 */

export interface PersonHeader {
  personKey: string;
  personType: string;
  lastName: string;
  firstName: string;
  dateOfBirth: string;
  city: string;
  departed: boolean;
  staffAllergies: string;
  allergyBanner: AllergyPanelData | null;
}
interface OtcItemView {
  itemKey: string;
  name: string;
  strength: string;
  form: string;
  unit: string;
  doseText: string;
  dosingIntervalHours: string;
  maxDosesPer24h: string;
  dosingSource: string;
  considerationPrompt: string;
  otcListItemId: string;
  otcName: string;
  use: string;
  topical: boolean;
  permission: "yes" | "no" | "call" | "staff";
  assistPermission: "yes" | "no" | "call" | null;
  onHand: number;
  stocked: boolean;
  defaultRoute: string;
  interval: { early: boolean; overMax: boolean; lastAtUtc: string; countIn24h: number; message: string };
}
interface CallView {
  notificationKey: string;
  toName: string;
  toContact: string;
  createdUtc: string;
  outcome: string;
  outcomeCode: string | null;
  otcListItemId: string;
  attempts: { atUtc: string; byName: string }[];
}
interface VisitView {
  visitKey: string;
  kind: string;
  startedAtUtc: string;
  complaint: string;
  status: string;
}
interface View {
  person: PersonHeader;
  rx: { medKey: string; name: string; dose: string; route: string; isPrn: boolean; status: MedStatus }[];
  items: OtcItemView[];
  authority: string | null;
  givers: { email: string; name: string; credential: string }[];
  me: { email: string; mayGive: boolean };
  today: AdminView[];
  calls: CallView[];
  openVisits: VisitView[];
  contacts: { name: string; relationship: string; phone: string }[];
  routes: string[];
  reasons: string[];
  callOutcomes: { code: string; label: string }[];
  canRecord: boolean;
  canStartVisit: boolean;
  visitKinds: string[];
}

export const PERMISSION_LABEL: Record<string, string> = { yes: "Yes", no: "No", call: "Call me first", staff: "Staff (no parent permission)" };
const PERMISSION_CLASS: Record<string, string> = { yes: "bg-emerald-100 text-emerald-900", no: "bg-slate-200 text-slate-700", call: "bg-amber-100 text-amber-950", staff: "bg-sky-100 text-sky-900" };
export const KIND_LABEL: Record<string, string> = { Injury: "Injury", Illness: "Illness", Other: "Other" };

const hhmm = () => {
  const m = easternMinutes(appNow());
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

export function PersonBanner({ p }: { p: PersonHeader }) {
  return (
    <>
      {p.allergyBanner && <AllergyBanner data={p.allergyBanner} />}
      {p.personType === "Staff" && (
        <div className={`mb-3 rounded-md border-2 px-3 py-2 text-sm ${p.staffAllergies ? "border-red-500 bg-red-50 text-red-950" : "border-slate-200 bg-white"}`}>
          <span className="font-semibold">Allergies (staff record):</span> {p.staffAllergies || "none listed"}
        </div>
      )}
    </>
  );
}

export function PermissionChip({ p }: { p: string }) {
  return <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${PERMISSION_CLASS[p] ?? ""}`}>{PERMISSION_LABEL[p] ?? p}</span>;
}

interface Form {
  itemKey: string;
  mode: "give" | "assist";
  indication: string;
  visitKey: string;
  disposition: string;
  amount: string;
  route: string;
  time: string;
  by: string;
  reason: string;
  intervalReason: string;
  considered: boolean;
  override: boolean;
  overrideReason: string;
  notificationKey: string;
  notes: string;
}

export function otcRules(f: Form, item: OtcItemView | undefined, permission: string, call: CallView | undefined): Rule[] {
  const given = f.disposition === "Given";
  const needsCall = permission === "call" || (permission === "no" && f.override);
  return [
    ["otc-item", !item, "Choose an OTC item from the list."],
    ["otc-override", Boolean(item) && permission === "no" && !f.override, "The parent said No to this item. To give it anyway, call the parent and log the call: “parent called and permitted”."],
    ["otc-call", Boolean(item) && needsCall && !call, permission === "call" ? "Call me first: log the call to the parent before this dose can be saved." : "The parent said No to this item. To give it anyway, call the parent and log the call: “parent called and permitted”."],
    ["otc-call", Boolean(item) && needsCall && Boolean(call) && given && call!.outcomeCode !== "Permitted", "The parent wasn't reached or declined: this can't be saved as given. Record it as held (parent not reached or declined)."],
    ["otc-ovr", Boolean(item) && permission === "no" && f.override && !f.overrideReason.trim(), "Write what the parent said (the override)."],
    ["otc-ind", Boolean(item) && !item!.topical && !f.indication.trim(), "Write the complaint (what it's for)."],
    ["otc-visit", Boolean(item) && !item!.topical && !f.visitKey, "An OTC given for a complaint goes with a visit: choose today's open visit, or start one."],
    ["otc-amt", given && !f.amount.trim(), "Write the amount given."],
    ["otc-route", given && !f.route.trim(), "Write the route."],
    ["otc-by", given && !f.by, "Choose who administered it from the list."],
    ["otc-consider", given && Boolean(item?.considerationPrompt) && !f.considered, "Tick the consideration before recording this dose."],
    ["otc-int", given && Boolean(item && (item.interval.early || item.interval.overMax)) && !f.intervalReason.trim(), "Write why it is given anyway (the interval warning)."],
    ["otc-reason", !given && !f.reason.trim(), "Write the reason."],
  ];
}

function CallLog({ v, item, onLogged }: { v: View; item: OtcItemView; onLogged: (c: CallView) => void }) {
  const [contact, setContact] = useState(v.contacts.find((c) => c.phone)?.phone ?? "");
  const [toName, setToName] = useState(v.contacts.find((c) => c.phone)?.name ?? "");
  const [time, setTime] = useState(hhmm());
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { problems, check } = useProblems([
    ["call-num", contact.replace(/\D/g, "").length < 7, "Enter the number dialled."],
    ["call-out", !outcome, "Choose the outcome: reached and permitted, reached and declined, reached with no decision, or no answer."],
  ]);
  async function log() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const r = await api<{ call: CallView }>("POST", `/api/otc/${encodeURIComponent(v.person.personKey)}/calls`, { supplyItemKey: item.itemKey, number: contact, toName, at: time, outcome, notes });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "The call wasn't logged.");
    onLogged({ ...r.data.call, outcomeCode: outcome, otcListItemId: item.otcListItemId });
  }
  return (
    <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3">
      <p className="text-sm font-semibold text-amber-950">Call the parent first</p>
      <p className="text-xs text-amber-950">Say who you are and the program, the complaint, the item you'd like to give and the dose; confirm permission or that they decline. Then log it.</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <div>
          <label htmlFor="call-num" className="field-label">Number dialled</label>
          <input id="call-num" className="field-input" type="tel" list="call-contacts" {...invalid("call-num", problems)} value={contact} onChange={(e) => { setContact(e.target.value); const c = v.contacts.find((x) => x.phone === e.target.value); if (c) setToName(c.name); }} />
          <datalist id="call-contacts">{v.contacts.filter((c) => c.phone).map((c) => <option key={c.phone} value={c.phone}>{`${c.name} (${c.relationship})`}</option>)}</datalist>
          <FieldProblem id="call-num" problems={problems} />
        </div>
        <div><label htmlFor="call-to" className="field-label">Spoke to (or tried)</label><input id="call-to" className="field-input" value={toName} onChange={(e) => setToName(e.target.value)} /></div>
        <div><label htmlFor="call-t" className="field-label">Time</label><input id="call-t" type="time" className="field-input" value={time} onChange={(e) => setTime(e.target.value)} /></div>
        <div>
          <label htmlFor="call-out" className="field-label">Outcome</label>
          <select id="call-out" className="field-input" {...invalid("call-out", problems)} value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            <option value="">Choose…</option>
            {v.callOutcomes.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
          </select>
          <FieldProblem id="call-out" problems={problems} />
        </div>
      </div>
      <label htmlFor="call-notes" className="field-label mt-2">What was said (optional)</label>
      <input id="call-notes" className="field-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
      {error && <p className="field-error" role="alert">{error}</p>}
      <button type="button" className="btn-secondary mt-2 min-h-11" disabled={busy} onClick={() => void log()}>Log the call</button>
    </div>
  );
}

function StartVisit({ v, complaint, onStarted }: { v: View; complaint: string; onStarted: (visitKey: string) => void }) {
  const [kind, setKind] = useState("Illness");
  const [text, setText] = useState(complaint);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function start() {
    setBusy(true);
    setError(null);
    const r = await api<{ visit: VisitView }>("POST", "/api/visits", { personKey: v.person.personKey, kind, complaint: text });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "The visit wasn't started.");
    onStarted(r.data.visit.visitKey);
  }
  return (
    <div className="mt-2 rounded-md border border-slate-300 p-3">
      <p className="text-sm font-semibold">New visit</p>
      <div className="mt-1 grid gap-2 sm:grid-cols-[10rem_1fr]">
        <div>
          <label htmlFor="nv-kind" className="field-label">Kind</label>
          <select id="nv-kind" className="field-input" value={kind} onChange={(e) => setKind(e.target.value)}>{v.visitKinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k] ?? k}</option>)}</select>
        </div>
        <div><label htmlFor="nv-c" className="field-label">Complaint</label><input id="nv-c" className="field-input" value={text} onChange={(e) => setText(e.target.value)} /></div>
      </div>
      {error && <p className="field-error" role="alert">{error}</p>}
      <button type="button" className="btn-secondary mt-2 min-h-11" disabled={busy || !text.trim()} onClick={() => void start()}>Start the visit</button>
      <p className="mt-1 text-xs text-slate-500">The rest of the visit (vitals, assessment, disposition) is recorded on the visit.</p>
    </div>
  );
}

export function Otc({ personKey }: { personKey: string }): React.ReactElement {
  const [v, setV] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const load = () => void api<View>("GET", `/api/otc/${encodeURIComponent(personKey)}`).then((r) => (r.ok ? (setV(r.data), setError(null)) : setError(r.data.error ?? "Could not load.")));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [personKey]);
  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!v) return <p className="text-sm text-slate-600">Loading…</p>;
  const p = v.person;
  const back = p.personType === "Staff" ? `/chart/${encodeURIComponent(p.personKey)}` : `/station/${encodeURIComponent(p.personKey)}`;
  return (
    <div>
      <p className="mb-3 text-sm"><a href={back} className="text-program underline">← {p.personType === "Staff" ? "Chart" : "Med station visit"}</a>{p.personType !== "Staff" && <> · <a href={`/pass/student/${encodeURIComponent(p.personKey)}`} className="text-program underline">Give now</a></>}</p>
      <PersonBanner p={p} />
      <section className="section-card">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-xl font-semibold">Give an OTC item: {p.lastName}, {p.firstName}</h2>
          <span className="text-sm text-slate-600">{p.personKey}{p.dateOfBirth ? ` · born ${p.dateOfBirth}` : ""}{p.personType === "Staff" ? " · staff" : ` · ${p.city || "Not assigned"}`}</span>
        </div>
        <p className="mt-1 text-sm"><span className="font-medium">OTC dosing authority:</span> {v.authority ?? <span className="text-amber-900">not set (Config: OTC dosing authority)</span>}</p>
        {p.personType === "Staff" && <p className="mt-1 text-sm text-sky-900">A staff member: no parent permission applies; the dose is recorded as given to staff.</p>}
        {p.departed && <p className="mt-1 text-sm font-semibold text-red-800">This student has left the session.</p>}
      </section>
      {saved && <p className="section-card text-sm text-emerald-800" role="status">{saved}</p>}
      {v.canRecord && !p.departed && <GiveForm key={v.today.length} v={v} onSaved={(msg) => { setSaved(msg); load(); }} onChanged={load} />}
      <section className="section-card">
        <h3 className="mb-1 text-base font-semibold">Current prescriptions</h3>
        {v.rx.length === 0 ? <p className="text-sm text-slate-600">None at the med station.</p> : (
          <ul className="text-sm">{v.rx.map((m) => <li key={m.medKey} className="flex flex-wrap items-center gap-2">{m.name} · {m.dose} · {m.route}{m.isPrn ? " · as needed" : ""} <MedStatusBadge status={m.status} /></li>)}</ul>
        )}
      </section>
      <section className="section-card">
        <h3 className="mb-1 text-base font-semibold">OTC permissions</h3>
        <ul className="grid gap-1 text-sm sm:grid-cols-2">
          {v.items.map((i) => (
            <li key={i.itemKey} className={`flex flex-wrap items-center justify-between gap-2 ${i.stocked ? "" : "text-slate-400"}`}>
              <span>{i.name}{i.strength ? ` ${i.strength}` : ""}{!i.stocked && " (none on hand)"}</span>
              <span className="flex gap-1"><PermissionChip p={i.permission} />{i.assistPermission && <span className="text-xs">assist: <PermissionChip p={i.assistPermission} /></span>}</span>
            </li>
          ))}
        </ul>
      </section>
      <section className="section-card">
        <h3 className="mb-1 text-base font-semibold">OTC today</h3>
        {v.today.length === 0 ? <p className="text-sm text-slate-600">None today.</p> : (
          <ul className="space-y-1 text-sm">{v.today.map((a) => <li key={a.adminKey}><span className="font-medium">{DISPOSITION_LABEL[a.disposition] ?? a.disposition}</span> · {a.amountGiven} · {stamp(a.givenAtUtc)} · {a.administeredByName || a.administeredByEmail}{a.reason ? ` · ${a.reason}` : ""}{a.countersigned === "N" && <span className="ml-1 rounded bg-amber-200 px-1 text-xs">unverified</span>}</li>)}</ul>
        )}
      </section>
    </div>
  );
}

function GiveForm({ v, onSaved, onChanged }: { v: View; onSaved: (msg: string) => void; onChanged: () => void }) {
  const [f, setF] = useState<Form>({ itemKey: "", mode: "give", indication: "", visitKey: v.openVisits[0]?.visitKey ?? "", disposition: "Given", amount: "", route: "", time: hhmm(), by: v.me.mayGive ? v.me.email : "", reason: "", intervalReason: "", considered: false, override: false, overrideReason: "", notificationKey: "", notes: "" });
  const [starting, setStarting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<Form>) => setF({ ...f, ...p });
  const item = v.items.find((i) => i.itemKey === f.itemKey);
  const permission = item ? (item.topical && f.mode === "assist" ? (item.assistPermission ?? "no") : item.permission) : "";
  const call = v.calls.find((c) => c.notificationKey === f.notificationKey) ?? (item ? v.calls.find((c) => c.otcListItemId === item.otcListItemId) : undefined);
  const { problems, check } = useProblems(otcRules(f, item, permission, call));
  const P = ({ k }: { k: string }) => <FieldProblem id={k} problems={problems} />;
  const offered = v.items.filter((i) => i.stocked && (i.permission !== "no" || (i.topical && i.assistPermission && i.assistPermission !== "no")));
  const refused = v.items.filter((i) => i.stocked && !offered.includes(i));
  const choose = (i: OtcItemView | undefined, override = false) => set({ itemKey: i?.itemKey ?? "", amount: i?.doseText ?? "", route: i?.defaultRoute ?? "", override, considered: false, intervalReason: "", notificationKey: "" });
  async function save() {
    setError(null);
    if (!check()) return;
    setBusy(true);
    const r = await api<{ adminKey: string }>("POST", `/api/otc/${encodeURIComponent(v.person.personKey)}/give`, {
      supplyItemKey: f.itemKey,
      mode: f.mode,
      indication: f.indication,
      visitKey: f.visitKey,
      disposition: f.disposition,
      amountGiven: f.amount,
      route: f.route,
      givenAt: f.time,
      administeredByEmail: f.by,
      reason: f.reason,
      intervalReason: f.intervalReason,
      considerationAcknowledged: f.considered,
      override: f.override,
      overrideReason: f.overrideReason,
      notificationKey: call?.notificationKey ?? "",
      notes: f.notes,
    });
    setBusy(false);
    if (!r.ok) return setError(r.data.error ?? "Not saved.");
    onSaved(`${item?.name ?? "OTC"}: ${DISPOSITION_LABEL[f.disposition] ?? f.disposition}, recorded.`);
  }
  return (
    <section className="section-card">
      <h3 className="mb-2 text-base font-semibold">Give</h3>
      {offered.length === 0 && refused.length === 0 ? <p className="text-sm text-amber-900">No OTC items are in stock (Supplies).</p> : (
        <>
          <div>
            <label htmlFor="otc-item" className="field-label">Item (in stock and permitted)</label>
            <select id="otc-item" className="field-input min-h-11" {...invalid("otc-item", problems)} value={f.override ? "" : f.itemKey} onChange={(e) => choose(v.items.find((i) => i.itemKey === e.target.value))}>
              <option value="">Choose…</option>
              {offered.map((i) => <option key={i.itemKey} value={i.itemKey}>{i.name}{i.strength ? ` ${i.strength}` : ""} — {PERMISSION_LABEL[i.permission]}{i.topical ? " (no visit needed)" : ""}</option>)}
            </select>
            <P k="otc-item" />
          </div>
          {refused.length > 0 && (
            <details className="mt-2 text-sm" open={f.override}>
              <summary className="cursor-pointer text-slate-600">The parent said No to {refused.length} stocked item{refused.length === 1 ? "" : "s"} (override only after a call)</summary>
              <ul className="mt-1 space-y-1">
                {refused.map((i) => (
                  <li key={i.itemKey} className="flex flex-wrap items-center justify-between gap-2 text-slate-500">
                    <span>{i.name}{i.strength ? ` ${i.strength}` : ""} <PermissionChip p="no" /></span>
                    <button type="button" className="text-program underline" onClick={() => choose(i, true)}>Parent called and permitted…</button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
      {item && (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-slate-700">
            {item.name}{item.strength ? ` ${item.strength}` : ""} · {item.otcName} ({item.use}) · <PermissionChip p={permission} />
            <span className="block text-xs text-slate-500">Dose {item.doseText || "as labeled"}{item.dosingIntervalHours ? ` · at least ${item.dosingIntervalHours} h apart` : ""}{item.maxDosesPer24h ? ` · at most ${item.maxDosesPer24h} in 24 h` : ""}{item.dosingSource ? ` · ${item.dosingSource === "StandingOrder" ? "standing order" : "product label"}` : ""} · {item.onHand} on hand</span>
          </p>
          {item.topical && (
            <div className="flex flex-wrap gap-x-4 text-sm" role="radiogroup" aria-label="Give or assist">
              <label className="flex items-center gap-1.5"><input type="radio" checked={f.mode === "give"} onChange={() => set({ mode: "give" })} />Staff apply it</label>
              <label className="flex items-center gap-1.5"><input type="radio" checked={f.mode === "assist"} onChange={() => set({ mode: "assist" })} />Assist the student</label>
            </div>
          )}
          {f.override && (
            <div>
              <label htmlFor="otc-ovr" className="field-label">Parent override: what the parent said <span className="text-red-600">*</span></label>
              <input id="otc-ovr" className="field-input" {...invalid("otc-ovr", problems)} value={f.overrideReason} onChange={(e) => set({ overrideReason: e.target.value })} />
              <P k="otc-ovr" />
            </div>
          )}
          {(permission === "call" || f.override) && (
            <div id="otc-call">
              {call ? (
                <p className={`rounded-md px-3 py-2 text-sm ${call.outcomeCode === "Permitted" ? "bg-emerald-50 text-emerald-900" : "bg-amber-50 text-amber-950"}`}>Call logged: {call.outcome} · {call.toName} {call.toContact} · {stamp(call.attempts[0]?.atUtc ?? call.createdUtc)}</p>
              ) : (
                <CallLog v={v} item={item} onLogged={() => onChanged()} />
              )}
              <P k="otc-call" />
            </div>
          )}
          {!item.topical && (
            <>
              <div>
                <label htmlFor="otc-ind" className="field-label">Complaint (what it's for) <span className="text-red-600">*</span></label>
                <input id="otc-ind" className="field-input" {...invalid("otc-ind", problems)} value={f.indication} onChange={(e) => set({ indication: e.target.value })} />
                <P k="otc-ind" />
              </div>
              <div>
                <label htmlFor="otc-visit" className="field-label">For which visit? <span className="text-red-600">*</span></label>
                <select id="otc-visit" className="field-input min-h-11" {...invalid("otc-visit", problems)} value={f.visitKey} onChange={(e) => (e.target.value === "new" ? setStarting(true) : set({ visitKey: e.target.value }))}>
                  <option value="">Choose…</option>
                  {v.openVisits.map((x) => <option key={x.visitKey} value={x.visitKey}>{KIND_LABEL[x.kind] ?? x.kind}: {x.complaint} ({stamp(x.startedAtUtc)})</option>)}
                  {v.canStartVisit && <option value="new">New visit…</option>}
                </select>
                <P k="otc-visit" />
                {starting && <StartVisit v={v} complaint={f.indication} onStarted={(k) => { setStarting(false); set({ visitKey: k }); onChanged(); }} />}
              </div>
            </>
          )}
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="What happened">
            {["Given", "Held", "Refused"].map((d) => (
              <button key={d} type="button" role="radio" aria-checked={f.disposition === d} className={`min-h-11 rounded-md border px-3 text-sm font-medium ${f.disposition === d ? "border-program bg-program text-white" : "border-slate-300 bg-white"}`} onClick={() => set({ disposition: d })}>{DISPOSITION_LABEL[d] ?? d}</button>
            ))}
          </div>
          {f.disposition === "Given" ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div><label htmlFor="otc-amt" className="field-label">Amount given</label><input id="otc-amt" className="field-input" {...invalid("otc-amt", problems)} value={f.amount} onChange={(e) => set({ amount: e.target.value })} /><P k="otc-amt" /></div>
              <div>
                <label htmlFor="otc-route" className="field-label">Route</label>
                <input id="otc-route" className="field-input" list="otc-routes" {...invalid("otc-route", problems)} value={f.route} onChange={(e) => set({ route: e.target.value })} />
                <datalist id="otc-routes">{v.routes.map((r) => <option key={r} value={r} />)}</datalist>
                <P k="otc-route" />
              </div>
              <div><label htmlFor="otc-t" className="field-label">Time</label><input id="otc-t" type="time" className="field-input" value={f.time} onChange={(e) => set({ time: e.target.value })} /></div>
              <div>
                <label htmlFor="otc-by" className="field-label">Administered by</label>
                <select id="otc-by" className="field-input" {...invalid("otc-by", problems)} value={f.by} onChange={(e) => set({ by: e.target.value })}>
                  <option value="">Choose…</option>
                  {v.givers.map((g) => <option key={g.email} value={g.email}>{g.name || g.email}{g.credential ? `, ${g.credential}` : ""}</option>)}
                </select>
                <P k="otc-by" />
                {f.by && f.by !== v.me.email && <p className="field-hint">Entered by you: {f.by} countersigns it with their own sign-in.</p>}
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="otc-reason" className="field-label">Reason <span className="text-red-600">*</span></label>
              <input id="otc-reason" className="field-input" list="otc-reasons" {...invalid("otc-reason", problems)} value={f.reason} onChange={(e) => set({ reason: e.target.value })} placeholder={call && call.outcomeCode !== "Permitted" ? "Parent not reached" : ""} />
              <datalist id="otc-reasons">{["Parent not reached", "Parent declined", ...v.reasons].map((r) => <option key={r} value={r} />)}</datalist>
              <P k="otc-reason" />
            </div>
          )}
          {f.disposition === "Given" && (item.interval.early || item.interval.overMax) && (
            <div className="rounded-md border border-amber-400 bg-amber-50 p-3" id="otc-int-box">
              <p className="text-sm font-semibold text-amber-950">Interval warning: {item.interval.message} This is a warning, not a block.</p>
              <label htmlFor="otc-int" className="field-label mt-1">Why it is given anyway <span className="text-red-600">*</span></label>
              <input id="otc-int" className="field-input" {...invalid("otc-int", problems)} value={f.intervalReason} onChange={(e) => set({ intervalReason: e.target.value })} />
              <P k="otc-int" />
            </div>
          )}
          {f.disposition === "Given" && item.considerationPrompt && (
            <div>
              <label className="flex items-start gap-2 rounded-md border border-slate-300 p-2 text-sm">
                <input id="otc-consider" type="checkbox" className="mt-1" {...invalid("otc-consider", problems)} checked={f.considered} onChange={(e) => set({ considered: e.target.checked })} />
                <span>{item.considerationPrompt}</span>
              </label>
              <P k="otc-consider" />
            </div>
          )}
          <div><label htmlFor="otc-notes" className="field-label">Note (optional)</label><input id="otc-notes" className="field-input" value={f.notes} onChange={(e) => set({ notes: e.target.value })} /></div>
          {error && <p className="field-error" role="alert">{error}</p>}
          <button type="button" className="btn-primary min-h-11" disabled={busy} onClick={() => void save()}>Record: {DISPOSITION_LABEL[f.disposition] ?? f.disposition}</button>
        </div>
      )}
    </section>
  );
}
